/* Shared Miles - storefront controller (vanilla, built on ApollithStudio / studio-core.js).
 *
 * "Two routes. One story." A customer adds TWO people's routes (an array of participants,
 * N-ready) and the distance they travelled together becomes one piece of wall art. Talks
 * to the same Route Studio backend through the signed App Proxy; the backend renders the
 * preview server-side and returns signed line-item properties. Raw coordinates never reach
 * the browser. All shared plumbing (proxy client, formatters, cart add, radiogroups) comes
 * from window.ApollithStudio so this file holds only the Shared-Miles-specific flow. */
(function () {
  'use strict';

  var S = window.ApollithStudio;
  var root = document.querySelector('[data-sm]');
  if (!root || !S) return;
  if (document.querySelectorAll('[data-sm]').length > 1 && window.console) {
    console.warn('[shared-miles] More than one Shared Miles section on the page; only the first is interactive.');
  }

  // ---- config (mirror of core/sharedMiles.mjs, emitted as one JSON blob) ----
  var CONFIG = S.parseJson(root.querySelector('[data-sm-config]')) || {};
  var PROXY = (root.dataset.proxy || '/apps/route-studio');
  var client = S.createClient(PROXY);
  var MAX_PARTICIPANTS = Math.max(2, CONFIG.maxParticipants || 2);
  var DEFAULT_STYLE = CONFIG.defaultStyle || 'sm-minimal';
  var DEFAULT_MODE = CONFIG.defaultMode || 'one-shared-adventure';
  var DEFAULT_PAIR = CONFIG.defaultPair || (CONFIG.pairs && CONFIG.pairs[0] && CONFIG.pairs[0].id) || '';
  var STRAVA = CONFIG.strava || {};
  var LEAFLET = CONFIG.leaflet || {};
  var STORY_MODES = CONFIG.modes || [];
  var STYLES = CONFIG.styles || [];
  var PAIRS = CONFIG.pairs || [];
  var MAX_UPLOAD = 10 * 1024 * 1024;
  var MAX_DRAW_POINTS = 50;
  var STADIA_TILES = 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png';
  var STADIA_ATTR = '&copy; <a href="https://stadiamaps.com/" target="_blank" rel="noopener">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

  var product = S.parseJson(root.querySelector('[data-sm-product]'));
  var DIGITAL = (product && product.digital) || {};
  var STYLE_NAME = {}; STYLES.forEach(function (s) { STYLE_NAME[s.id] = s.name; });
  var PAIR_BY_ID = {}; PAIRS.forEach(function (p) { PAIR_BY_ID[p.id] = p; });
  var MODE_BY_ID = {}; STORY_MODES.forEach(function (m) { MODE_BY_ID[m.id] = m; });

  // Whether the active story mode contributes one activity per person (One Shared
  // Adventure) or many (Miles Together / Our First Miles). Drives single-vs-append import.
  function modeSingle() { var m = MODE_BY_ID[state.story.mode]; return !m || m.activities !== 'many'; }

  // ---- state ---------------------------------------------------------------
  function newParticipant(slot) {
    return {
      slot: slot, id: null, name: '', colour: '', label: 'Person ' + (slot + 1),
      hasRoute: false, source: null, routeName: null, distanceMetres: null, activityCount: 0,
      sameAs: false, selectedActivityId: null, selectedPopularId: null
    };
  }
  var state = {
    jobId: null, ready: null, activeSlot: 0,
    story: { mode: DEFAULT_MODE, colourPreset: DEFAULT_PAIR, showParticipantTimes: false, markers: { start: false, finish: false, meeting: false } },
    participants: [newParticipant(0), newParticipant(1)],
    design: {
      styleId: DEFAULT_STYLE,
      size: 'A4', presentation: 'print-only', digitalCopy: false, distanceUnit: 'miles',
      text: { title: '', subtitle: '', date: '', dedication: '', location: '', relationship: '', activityName: '' }
    },
    edited: {},
    preview: { token: 0, attempt: 0, rendered: false, failed: false },
    saving: false, pendingSave: false, pendingPreview: false, patchInFlight: 0, saveGen: 0,
    addingToCart: false, addedToCart: false,
    strava: { list: [], search: '', sort: 'recent' },
    popular: { loaded: null, search: '' },
    draw: { map: null, layer: null, pins: [], markers: [], snapLine: null, rawLine: null, snapTimer: null, snapToken: 0, ready: false, lastKey: '', lastDistance: null, committing: false, tileErrors: 0 }
  };

  // ---- dom -----------------------------------------------------------------
  function qs(sel, ctx) { return (ctx || root).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function show(n) { if (n) n.hidden = false; }
  function hide(n) { if (n) n.hidden = true; }
  function esc(s) { return S.esc(s); }
  var el = {
    modeRow: qs('[data-sm-modes]'),
    slotsWrap: qs('[data-sm-slots]'),
    slotTemplate: qs('[data-sm-slot-template]'),
    // shared route-source picker (targets state.activeSlot)
    picker: qs('[data-sm-picker]'),
    pickerTitle: qs('[data-sm-picker-title]'),
    pickerClose: qs('[data-sm-picker-close]'),
    stravaPanel: qs('[data-sm-strava-panel]'),
    stravaList: qs('[data-sm-strava-list]'),
    stravaHint: qs('[data-sm-strava-hint]'),
    stravaSearch: qs('[data-sm-strava-search]'),
    stravaReconnect: qs('[data-sm-strava-reconnect]'),
    popularPanel: qs('[data-sm-popular-panel]'),
    popularList: qs('[data-sm-popular-list]'),
    popularSearch: qs('[data-sm-popular-search]'),
    drawPanel: qs('[data-sm-draw-panel]'),
    drawMapEl: qs('[data-sm-draw-map]'),
    drawAdd: qs('[data-sm-draw-add]'),
    drawUndo: qs('[data-sm-draw-undo]'),
    drawClear: qs('[data-sm-draw-clear]'),
    drawUse: qs('[data-sm-draw-use]'),
    drawReadout: qs('[data-sm-draw-readout]'),
    drawError: qs('[data-sm-draw-error]'),
    drawMapFail: qs('[data-sm-draw-mapfail]'),
    // customise
    styleRow: qs('[data-sm-styles]'),
    pairRow: qs('[data-sm-pairs]'),
    markersRow: qs('[data-sm-markers]'),
    unitRow: qs('[data-sm-units]'),
    sizeRow: qs('[data-sm-sizes]'),
    presentationRow: qs('[data-sm-presentations]'),
    digital: qs('[data-sm-digital]'),
    digitalRow: qs('[data-sm-digital-row]'),
    addonPrice: qs('[data-sm-addon-price]'),
    // preview
    previewImg: qs('[data-sm-preview-img]'),
    posterEmpty: qs('[data-sm-poster-empty]'),
    posterExample: qs('[data-sm-poster-example]'),
    posterLoading: qs('[data-sm-poster-loading]'),
    posterLoadingNote: qs('[data-sm-poster-loading-note]'),
    previewError: qs('[data-sm-preview-error]'),
    previewErrorText: qs('[data-sm-preview-error-text]'),
    previewRetry: qs('[data-sm-preview-retry]'),
    stage: qs('[data-sm-stage]'),
    poster: qs('[data-sm-poster]'),
    zoom: qs('[data-sm-zoom]'),
    zoomImg: qs('[data-sm-zoom-img]'),
    // bar
    bar: qs('[data-sm-bar]'),
    barError: qs('[data-sm-bar-error]'),
    sumTotals: qsa('[data-sm-sum-total]'),
    sumStyle: qs('[data-sm-sum-style]'),
    sumSize: qs('[data-sm-sum-size]'),
    primaries: qsa('[data-sm-primary]'),
    primaryLabels: qsa('[data-sm-primary-label]'),
    // gift
    giftToggle: qs('[data-sm-gift-toggle]'),
    giftFields: qs('[data-sm-gift-fields]'),
    giftMessage: qs('[data-sm-gift-message]'),
    // acknowledgement
    permission: qs('[data-sm-permission]')
  };

  // ---- analytics (PII-free; no names, no coordinates) ----------------------
  // Fires GA4-convention events to window.dataLayer (+ Shopify Customer Events if present).
  // product-specific dimensions let Shared Miles be compared with Routes without merging.
  function track(event, extra) {
    var payload = { event: event, product: 'shared-miles', story_mode: state.story.mode, style_id: state.design.styleId, size: state.design.size, presentation: state.design.presentation, digital: !!state.design.digitalCopy, participant_count: state.participants.length };
    if (extra) for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]; }
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) { /* noop */ }
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish('shared_miles_' + event, payload); } catch (e) { /* noop */ }
  }

  // ---- job + participants bootstrap ---------------------------------------
  var SESSION = S.makeSession('sm-session');
  function ensureReady() {
    if (state.ready) return state.ready;
    state.ready = client.request('/jobs', { method: 'POST', json: { kind: 'shared-miles' } })
      .then(function (data) {
        state.jobId = data.publicId; SESSION.set({ jobId: state.jobId });
        // Create the participant slots on the server, in order, and adopt their ids.
        var chain = Promise.resolve();
        state.participants.forEach(function (p, i) {
          chain = chain.then(function () {
            return client.request('/jobs/' + state.jobId + '/participants', { method: 'POST', json: { label: p.label } })
              .then(function (st) { state.participants[i].id = st.participantId; });
          });
        });
        return chain.then(function () { return state.jobId; });
      })
      .catch(function (e) { state.ready = null; throw e; });
    return state.ready;
  }

  // ---- story mode ----------------------------------------------------------
  function selectMode(id) {
    if (!MODE_BY_ID[id]) return;
    state.story.mode = id;
    qsa('[data-sm-mode]', el.modeRow).forEach(function (b) {
      var on = b.dataset.smMode === id;
      b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1;
    });
    track('story_mode_select');
    scheduleSave();
  }

  // ---- participant slots ---------------------------------------------------
  function renderSlots() {
    if (!el.slotsWrap || !el.slotTemplate) return;
    el.slotsWrap.innerHTML = '';
    state.participants.forEach(function (p) {
      var node = el.slotTemplate.content ? el.slotTemplate.content.cloneNode(true) : el.slotTemplate.cloneNode(true);
      var card = node.querySelector ? node.querySelector('[data-sm-slot]') : node;
      var slot = p.slot;
      qs('[data-sm-slot-heading]', card).textContent = p.name ? p.name + '’s route' : 'Add ' + (slot === 0 ? 'the first' : 'the second') + ' route';
      var nameInput = qs('[data-sm-slot-name]', card);
      nameInput.value = p.name || '';
      nameInput.addEventListener('input', function () { setParticipantName(slot, nameInput.value); });
      // route status
      var status = qs('[data-sm-slot-status]', card);
      if (p.hasRoute) { status.textContent = p.sameAs ? 'Using the same route' : ('Added' + (p.routeName ? ': ' + p.routeName : '') + (typeof p.distanceMetres === 'number' ? ' · ' + S.fmt.distanceWithUnit(p.distanceMetres, state.design.distanceUnit) : '')); status.classList.add('is-set'); }
      else { status.textContent = 'No route yet'; status.classList.remove('is-set'); }
      // source buttons
      qsa('[data-sm-source]', card).forEach(function (b) {
        b.addEventListener('click', function () { openSource(slot, b.dataset.smSource); });
      });
      var gpx = qs('[data-sm-gpx-input]', card);
      if (gpx) gpx.addEventListener('change', function () { var f = gpx.files && gpx.files[0]; gpx.value = ''; if (f) uploadGpx(slot, f); });
      // "we did the same activity" (slot 2+ only, when the other slot has a real route)
      var same = qs('[data-sm-same]', card);
      if (same) {
        var canSame = slot > 0 && state.participants[0].id && state.participants[0].hasRoute && !state.participants[0].sameAs;
        same.closest('[data-sm-same-row]').hidden = !canSame && !p.sameAs;
        var chk = qs('input', same) || same;
        chk.checked = !!p.sameAs;
        chk.addEventListener('change', function () { useSameRoute(slot, chk.checked); });
      }
      var removeBtn = qs('[data-sm-slot-remove-route]', card);
      if (removeBtn) { removeBtn.hidden = !p.hasRoute || p.sameAs; removeBtn.addEventListener('click', function () { removeRoute(slot); }); }
      el.slotsWrap.appendChild(node);
    });
  }

  function setParticipantName(slot, value) {
    state.participants[slot].name = value.slice(0, 40);
    // A slot heading uses the name; refresh only that heading cheaply.
    var cards = qsa('[data-sm-slot]', el.slotsWrap);
    if (cards[slot]) { var h = qs('[data-sm-slot-heading]', cards[slot]); if (h) h.textContent = value ? value + '’s route' : 'Add ' + (slot === 0 ? 'the first' : 'the second') + ' route'; }
    scheduleSave(false); // a name change re-renders the preview (it appears in participant labels/markers only indirectly; save + light preview)
  }

  function openSource(slot, source) {
    state.activeSlot = slot;
    hide(el.stravaPanel); hide(el.popularPanel); hide(el.drawPanel);
    show(el.picker);
    if (el.pickerTitle) el.pickerTitle.textContent = 'Add ' + (state.participants[slot].name || (slot === 0 ? 'the first' : 'the second')) + ' route';
    track('route_source_select', { source: source, slot: slot });
    if (source === 'strava') {
      if (!STRAVA.enabled || !STRAVA.start) { if (el.stravaHint) el.stravaHint.textContent = 'Strava import is not available yet. Please upload a GPX file.'; }
      show(el.stravaPanel);
      if (!state.jobId) { connectStrava(slot); return; }
      loadActivities();
    } else if (source === 'popular') { show(el.popularPanel); loadPopular(); }
    else if (source === 'draw') { show(el.drawPanel); openDraw(); }
  }
  function closePicker() { hide(el.picker); }

  // Import success handler, shared by every source.
  function onImported(slot, st) {
    var p = state.participants[slot];
    var srv = (st.participants || []).filter(function (x) { return x.id === p.id; })[0];
    p.hasRoute = srv ? srv.hasRoute : true;
    p.sameAs = srv ? !!srv.sameAs : false;
    p.activityCount = srv ? srv.activityCount : 1;
    var act = srv && srv.activities && srv.activities[0];
    p.source = act ? act.sourceType : (st.sourceType || null);
    p.routeName = act ? act.routeName : null;
    p.distanceMetres = act && typeof act.distanceMetres === 'number' ? act.distanceMetres : null;
    // Reconcile the other participants from the server too: a "same route" cascade or a
    // single-activity mode can change another slot's readiness server-side.
    (st.participants || []).forEach(function (sp) {
      var q = state.participants.filter(function (x) { return x.id === sp.id; })[0];
      if (!q || q === p) return;
      q.hasRoute = sp.hasRoute; q.sameAs = !!sp.sameAs; q.activityCount = sp.activityCount || 0;
      var a2 = sp.activities && sp.activities[0];
      if (a2) { q.source = a2.sourceType; q.routeName = a2.routeName; if (typeof a2.distanceMetres === 'number') q.distanceMetres = a2.distanceMetres; }
    });
    // Seed a subtitle default from the two names once both have one.
    maybeSeedText();
    renderSlots();
    closePicker();
    track('route_import_success', { source: p.source || 'unknown', slot: slot });
    // Both routes in: save + render the full preview (the working veil stays until it lands).
    // Only one so far: persist it but restore the example, so the veil shown at import start
    // never spins forever (renderPreview bails until both routes are ready).
    if (bothReady()) { scheduleSave(true); }
    else { showExamplePoster(); scheduleSave(true, false); }
    updateGate();
  }
  function onImportError(slot, e) {
    track('route_import_error', { slot: slot, code: (e && e.code) || 'error' });
    markPreviewError(new Error((e && e.message) || 'We could not use that route. Please try another.'));
  }

  // GPX upload for a slot.
  function uploadGpx(slot, file) {
    if (!/\.gpx$/i.test(file.name)) { return markPreviewError(new Error('That is not a GPX file. Please choose a file ending in .gpx.')); }
    if (file.size > MAX_UPLOAD) { return markPreviewError(new Error('That file is too large. GPX files are usually well under 10 MB.')); }
    showPreviewWorking();
    ensureReady()
      .then(function () {
        var pid = state.participants[slot].id;
        return client.request('/jobs/' + state.jobId + '/participants/' + pid + '/upload-gpx?filename=' + encodeURIComponent(file.name) + '&single=' + modeSingle(), { method: 'POST', headers: { 'Content-Type': 'application/gpx+xml' }, body: file, timeoutMs: 60000 });
      })
      .then(function (st) { onImported(slot, st); })
      .catch(function (e) { onImportError(slot, e); });
  }

  function useSameRoute(slot, on) {
    ensureReady().then(function () {
      var pid = state.participants[slot].id;
      var otherId = state.participants[0].id;
      return client.request('/jobs/' + state.jobId + '/participants/' + pid + '/same-as', { method: 'POST', json: { sameAsId: on ? otherId : null } });
    }).then(function (st) {
      var p = state.participants[slot];
      var srv = (st.participants || []).filter(function (x) { return x.id === p.id; })[0];
      p.sameAs = srv ? !!srv.sameAs : on; p.hasRoute = srv ? srv.hasRoute : on;
      renderSlots();
      if (bothReady()) { scheduleSave(true); }
      else { showExamplePoster(); scheduleSave(true, false); }
      updateGate();
      track('same_route_toggle', { slot: slot, on: !!on });
    }).catch(function (e) { markPreviewError(new Error((e && e.message) || 'We could not share that route.')); renderSlots(); });
  }

  function removeRoute(slot) {
    // Removing a slot's activity: re-import replaces, so "remove" clears by importing
    // nothing is not an endpoint; instead we let the customer re-import. Here we simply
    // reset the local flag and prompt a fresh import (server keeps the last until replaced).
    openSource(slot, 'strava'); // reopen the picker so they can choose a new route
  }

  // ---- Strava (serial: the job holds one athlete's token at a time) --------
  function connectStrava(slot) {
    if (!STRAVA.enabled || !STRAVA.start) { if (el.stravaHint) el.stravaHint.textContent = 'Strava is not available yet. Please upload a GPX file.'; return; }
    try { SESSION.set({ jobId: state.jobId, activeSlot: slot, design: state.design, story: state.story }); } catch (e) {}
    ensureReady().then(function () {
      var sep = STRAVA.start.indexOf('?') >= 0 ? '&' : '?';
      window.location.href = STRAVA.start + sep + 'jobId=' + encodeURIComponent(state.jobId);
    }).catch(function (e) { if (el.stravaHint) el.stravaHint.textContent = (e && e.message) || 'Could not start Strava.'; });
  }
  function loadActivities() {
    if (!state.jobId) return;
    el.stravaList.setAttribute('aria-busy', 'true');
    el.stravaList.innerHTML = '<div class="sm__loading"><span class="sm__spinner" aria-hidden="true"></span><span>Loading your activities…</span></div>';
    client.request('/jobs/' + state.jobId + '/strava-activities').then(function (data) {
      if (data && (data.unitSystem === 'imperial' || data.unitSystem === 'metric')) { state.design.distanceUnit = data.unitSystem === 'imperial' ? 'miles' : 'kilometres'; syncUnitUI(); }
      state.strava.list = (data && data.activities) || [];
      renderActivities();
    }).catch(function (e) {
      el.stravaList.setAttribute('aria-busy', 'false'); el.stravaList.innerHTML = '';
      if (el.stravaHint) el.stravaHint.textContent = (e.code === 'strava_expired' || e.code === 'not_connected') ? 'Your Strava session has ended. Reconnect to choose an activity.' : (e.message || 'We could not load your Strava activities.');
      show(el.stravaReconnect);
    });
  }
  function renderActivities() {
    var q = (state.strava.search || '').trim().toLowerCase();
    var view = state.strava.list.filter(function (a) { return a.hasGps && (!q || String(a.name || '').toLowerCase().indexOf(q) >= 0); });
    el.stravaList.setAttribute('aria-busy', 'false');
    el.stravaList.innerHTML = '';
    if (!view.length) { el.stravaList.innerHTML = '<p class="sm__muted">No activities with GPS match. Try another search or upload a GPX file.</p>'; return; }
    var unit = state.design.distanceUnit;
    view.forEach(function (a) {
      var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'sm__activity';
      var meta = [];
      if (a.date) meta.push(S.fmt.date(a.date));
      if (typeof a.distanceMetres === 'number') meta.push(S.fmt.distanceWithUnit(a.distanceMetres, unit));
      if (a.sportType) meta.push(a.sportType);
      btn.innerHTML = '<span class="sm__activity-name">' + esc(a.name) + '</span><span class="sm__activity-meta">' + esc(meta.join(' · ')) + '</span>';
      btn.addEventListener('click', function () { importStrava(state.activeSlot, a.id, btn); });
      el.stravaList.appendChild(btn);
    });
  }
  function importStrava(slot, activityId, btn) {
    if (btn) btn.classList.add('is-busy');
    qsa('.sm__activity', el.stravaList).forEach(function (b) { b.disabled = true; });
    showPreviewWorking();
    var pid = state.participants[slot].id;
    client.request('/jobs/' + state.jobId + '/participants/' + pid + '/import-strava-activity?single=' + modeSingle(), { method: 'POST', json: { activityId: activityId }, timeoutMs: 35000 })
      .then(function (st) { onImported(slot, st); })
      .catch(function (e) { qsa('.sm__activity', el.stravaList).forEach(function (b) { b.disabled = false; }); onImportError(slot, e); });
  }

  // ---- popular routes ------------------------------------------------------
  function loadPopular() {
    if (state.popular.loaded) { renderPopular(); return; }
    el.popularList.setAttribute('aria-busy', 'true');
    client.request('/popular-routes').then(function (data) { state.popular.loaded = (data && data.routes) || []; renderPopular(); })
      .catch(function () { el.popularList.setAttribute('aria-busy', 'false'); el.popularList.innerHTML = '<p class="sm__muted">We could not load the routes. Please upload a GPX file instead.</p>'; });
  }
  function renderPopular() {
    var q = (state.popular.search || '').trim().toLowerCase();
    var view = (state.popular.loaded || []).filter(function (r) { return !q || (String(r.name) + ' ' + String(r.place)).toLowerCase().indexOf(q) >= 0; });
    el.popularList.setAttribute('aria-busy', 'false');
    el.popularList.innerHTML = '';
    view.forEach(function (r) {
      var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'sm__activity';
      var meta = [r.place]; if (typeof r.distanceKm === 'number') meta.push(r.distanceKm + ' km'); if (r.sportType) meta.push(r.sportType);
      btn.innerHTML = '<span class="sm__activity-name">' + esc(r.name) + '</span><span class="sm__activity-meta">' + esc(meta.join(' · ')) + '</span>';
      btn.addEventListener('click', function () { importPopular(state.activeSlot, r.id, btn); });
      el.popularList.appendChild(btn);
    });
  }
  function importPopular(slot, routeId, btn) {
    if (btn) btn.classList.add('is-busy');
    qsa('.sm__activity', el.popularList).forEach(function (b) { b.disabled = true; });
    showPreviewWorking();
    ensureReady().then(function () {
      var pid = state.participants[slot].id;
      return client.request('/jobs/' + state.jobId + '/participants/' + pid + '/import-popular-route?single=' + modeSingle(), { method: 'POST', json: { routeId: routeId }, timeoutMs: 35000 });
    }).then(function (st) { onImported(slot, st); })
      .catch(function (e) { qsa('.sm__activity', el.popularList).forEach(function (b) { b.disabled = false; }); onImportError(slot, e); });
  }

  // ---- draw your own route (Leaflet, lazy-loaded) --------------------------
  var leafletPromise = null;
  function ensureLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    if (!LEAFLET.js) return Promise.reject(new Error('Map library is unavailable.'));
    leafletPromise = new Promise(function (resolve, reject) {
      if (LEAFLET.css && !document.querySelector('link[data-sm-leaflet]')) { var link = document.createElement('link'); link.rel = 'stylesheet'; link.href = LEAFLET.css; link.setAttribute('data-sm-leaflet', ''); document.head.appendChild(link); }
      var s = document.createElement('script'); s.src = LEAFLET.js; s.async = true;
      s.onload = function () { if (window.L) resolve(window.L); else { leafletPromise = null; reject(new Error('Map library failed to load.')); } };
      s.onerror = function () { leafletPromise = null; reject(new Error('Map library failed to load.')); };
      document.head.appendChild(s);
    });
    return leafletPromise;
  }
  function openDraw() { ensureReady().catch(function () {}); ensureLeaflet().then(initDraw).catch(function () { if (el.drawMapFail) show(el.drawMapFail); }); }
  function initDraw() {
    var L = window.L; if (!L || !el.drawMapEl) return;
    if (state.draw.map) { setTimeout(function () { state.draw.map.invalidateSize(); }, 60); return; }
    var map = L.map(el.drawMapEl, { center: [54.0, -2.6], zoom: 6, doubleClickZoom: false });
    var tiles = L.tileLayer(STADIA_TILES, { maxZoom: 19, attribution: STADIA_ATTR, crossOrigin: true }).addTo(map);
    state.draw.layer = L.layerGroup().addTo(map);
    map.on('click', function (e) { addPin(e.latlng); });
    tiles.on('tileerror', function () { state.draw.tileErrors++; if (state.draw.tileErrors === 8 && el.drawMapFail) show(el.drawMapFail); });
    state.draw.map = map;
    setTimeout(function () { map.invalidateSize(); }, 60);
  }
  function rebuildPins() {
    var L = window.L; if (!L || !state.draw.map) return;
    state.draw.markers.forEach(function (m) { state.draw.layer.removeLayer(m); });
    state.draw.markers = [];
    state.draw.pins.forEach(function (pin, i) {
      var icon = L.divIcon({ className: 'sm-pin', html: '<span class="sm-pin__dot">' + (i + 1) + '</span>', iconSize: [30, 30], iconAnchor: [15, 15] });
      var m = L.marker([pin.lat, pin.lng], { draggable: true, icon: icon });
      m.on('dragend', function () { var ll = m.getLatLng(); pin.lat = ll.lat; pin.lng = ll.lng; scheduleSnap(); });
      m.on('click', function (ev) { L.DomEvent.stopPropagation(ev); state.draw.pins.splice(i, 1); rebuildPins(); scheduleSnap(); });
      m.addTo(state.draw.layer); state.draw.markers.push(m);
    });
    if (el.drawUndo) el.drawUndo.disabled = state.draw.pins.length < 1;
    if (el.drawClear) el.drawClear.disabled = state.draw.pins.length < 1;
  }
  function addPin(latlng) {
    if (state.draw.pins.length >= MAX_DRAW_POINTS) return;
    state.draw.pins.push({ lat: latlng.lat, lng: latlng.lng }); rebuildPins(); scheduleSnap();
  }
  function drawReadout(txt) { if (el.drawReadout) el.drawReadout.textContent = txt; }
  function snappedLine(line) {
    if (!state.draw.map || !Array.isArray(line)) return;
    if (state.draw.snapLine) state.draw.layer.removeLayer(state.draw.snapLine);
    var latlngs = line.map(function (c) { return [c[1], c[0]]; });
    state.draw.snapLine = window.L.polyline(latlngs, { color: '#c0512c', weight: 4, opacity: 0.95, interactive: false }).addTo(state.draw.layer);
  }
  function scheduleSnap() {
    if (state.draw.snapTimer) clearTimeout(state.draw.snapTimer);
    state.draw.ready = false; if (el.drawUse) el.drawUse.disabled = true;
    if (state.draw.pins.length < 2) { drawReadout('Tap the map to add points'); return; }
    state.draw.snapTimer = setTimeout(runSnap, 450);
  }
  function runSnap() {
    var token = ++state.draw.snapToken;
    var points = state.draw.pins.map(function (p) { return [p.lng, p.lat]; });
    drawReadout('Tracing the roads…');
    ensureReady().then(function () { return client.request('/jobs/' + state.jobId + '/draw-route', { method: 'POST', json: { points: points } }); })
      .then(function (data) { if (token !== state.draw.snapToken) return; if (data.line) snappedLine(data.line); state.draw.ready = true; state.draw.lastDistance = data.distanceMetres; if (el.drawUse) el.drawUse.disabled = false; drawReadout(state.draw.pins.length + ' points · tap "Use this route"'); })
      .catch(function () { if (token === state.draw.snapToken) drawReadout('We could not trace that. Move a point and try again.'); });
  }
  function useDrawn() {
    if (!state.draw.ready || state.draw.committing) return;
    state.draw.committing = true; if (el.drawUse) el.drawUse.disabled = true;
    showPreviewWorking();
    var slot = state.activeSlot; var points = state.draw.pins.map(function (p) { return [p.lng, p.lat]; });
    ensureReady().then(function () { var pid = state.participants[slot].id; return client.request('/jobs/' + state.jobId + '/participants/' + pid + '/draw-route/commit?single=' + modeSingle(), { method: 'POST', json: { points: points }, timeoutMs: 35000 }); })
      .then(function (st) { state.draw.committing = false; onImported(slot, st); })
      .catch(function (e) { state.draw.committing = false; onImportError(slot, e); });
  }

  // ---- design: styles, colour pairs, text, size/frame/digital, units -------
  function selectStyle(id) {
    state.design.styleId = id;
    qsa('[data-sm-style]', el.styleRow).forEach(function (b) { var on = b.dataset.smStyle === id; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
    if (el.sumStyle) el.sumStyle.textContent = STYLE_NAME[id] || id;
    track('style_select');
    scheduleSave();
  }
  function selectPair(id) {
    state.story.colourPreset = id;
    qsa('[data-sm-pair]', el.pairRow).forEach(function (b) { var on = b.dataset.smPair === id; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
    track('colour_change', { pair: id });
    scheduleSave();
  }
  function selectSize(size) {
    state.design.size = size;
    qsa('[data-sm-size]', el.sizeRow).forEach(function (b) { var on = b.dataset.smSize === size; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
    if (el.stage) el.stage.dataset.smSize = size;
    if (el.sumSize) el.sumSize.textContent = size;
    updateGate(); renderTotals(); scheduleSave(false, false); // size never changes the artwork
  }
  function selectPresentation(p) {
    state.design.presentation = p;
    qsa('[data-sm-presentation]', el.presentationRow).forEach(function (b) { var on = b.dataset.smPresentation === p; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
    if (el.stage) el.stage.classList.toggle('is-framed', p === 'framed');
    updateGate(); renderTotals(); scheduleSave(false, false);
  }
  function syncUnitUI() { qsa('[data-sm-unit]', el.unitRow).forEach(function (b) { var on = b.dataset.smUnit === state.design.distanceUnit; b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; }); }
  function selectUnit(u) { if (state.design.distanceUnit === u) return; state.design.distanceUnit = u; syncUnitUI(); renderSlots(); scheduleSave(); }
  // Seed a friendly subtitle default once both names are known and the customer has not typed one.
  function maybeSeedText() {
    if (state.edited.subtitle) return;
    var a = state.participants[0] && state.participants[0].name;
    var b = state.participants[1] && state.participants[1].name;
    if (a && b) { state.design.text.subtitle = a + ' & ' + b; var i = qs('[data-sm-text="subtitle"]'); if (i && !i.value) i.value = state.design.text.subtitle; }
  }

  // ---- design build + save + preview (serialised) --------------------------
  function buildDesign() {
    var t = state.design.text;
    return {
      kind: 'shared-miles',
      styleId: state.design.styleId,
      text: { title: t.title, subtitle: t.subtitle, date: t.date, distanceUnit: state.design.distanceUnit },
      product: { size: state.design.size, presentation: state.design.presentation, digitalCopy: !!state.design.digitalCopy },
      sharedMiles: {
        story: { mode: state.story.mode, colourPreset: state.story.colourPreset, showParticipantTimes: !!state.story.showParticipantTimes, markers: state.story.markers },
        participants: state.participants.filter(function (p) { return p.id; }).map(function (p) { return { id: p.id, name: p.name || undefined, label: p.label || undefined, colour: p.colour || undefined }; }),
        text: { dedication: t.dedication, location: t.location, relationship: t.relationship, activityName: t.activityName }
      }
    };
  }
  function bothReady() { return state.participants.length >= 2 && state.participants.every(function (p) { return p.hasRoute; }); }

  var saveTimer = null;
  function scheduleSave(immediate, withPreview) {
    if (withPreview === undefined) withPreview = true;
    if (withPreview) { state.pendingPreview = true; if (bothReady()) showPreviewWorking(); }
    if (saveTimer) clearTimeout(saveTimer);
    if (immediate) { runSave(); return; }
    saveTimer = setTimeout(runSave, 500);
  }
  function runSave() {
    if (!state.jobId) return;
    if (state.saving) { state.pendingSave = true; return; }
    state.saving = true;
    var gen = state.saveGen;
    var doPreview = state.pendingPreview && bothReady();
    state.pendingPreview = false;
    state.patchInFlight++;
    var patch = client.request('/jobs/' + state.jobId + '/design', { method: 'PATCH', json: buildDesign() });
    patch.then(function () { state.patchInFlight--; }, function () { state.patchInFlight--; });
    patch.then(function () { if (gen !== state.saveGen) return null; return doPreview ? renderPreview() : null; })
      .catch(function (e) { if (gen === state.saveGen && doPreview) markPreviewError(e); })
      .then(function () { if (gen !== state.saveGen) return; state.saving = false; if (state.pendingSave) { state.pendingSave = false; runSave(); } });
  }
  function flushDesign() {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    state.pendingSave = false;
    return new Promise(function (resolve) { var w = 0; var iv = setInterval(function () { w += 100; if (!(state.patchInFlight > 0) || w >= 3000) { clearInterval(iv); resolve(); } }, 100); })
      .then(function () { return client.request('/jobs/' + state.jobId + '/design', { method: 'PATCH', json: buildDesign() }); });
  }

  function renderPreview() {
    if (!state.jobId || !bothReady()) return Promise.resolve();
    var token = ++state.preview.token;
    hidePreviewError(); hidePreviewNote(); show(el.posterLoading);
    var soft = setTimeout(function () { if (token === state.preview.token && !state.preview.rendered) show(el.posterLoadingNote); }, 8000);
    var watchdog = setTimeout(function () { if (token === state.preview.token && !state.preview.rendered) failPreview(new Error('The preview is taking too long. Please try again.')); }, 65000);
    var clear = function () { clearTimeout(soft); clearTimeout(watchdog); };
    return client.request('/jobs/' + state.jobId + '/preview', { method: 'POST', timeoutMs: 55000 }).then(function (data) {
      if (token !== state.preview.token) { clear(); return; }
      if (data && data.previewUrl) {
        var img = new Image();
        img.onload = function () {
          clear(); if (token !== state.preview.token) return;
          el.previewImg.src = data.previewUrl; el.previewImg.alt = previewAlt();
          var wasHidden = el.previewImg.hidden; el.previewImg.hidden = false;
          el.previewImg.classList.remove('sm-anim-in', 'sm-anim-refresh'); void el.previewImg.offsetWidth; el.previewImg.classList.add(wasHidden ? 'sm-anim-in' : 'sm-anim-refresh');
          hide(el.posterEmpty); hide(el.posterExample); hide(el.posterLoading); hidePreviewNote(); hidePreviewError();
          state.preview.rendered = true; state.preview.failed = false; state.preview.attempt = 0;
          if (el.stage) el.stage.classList.add('is-zoomable');
          track('proof_complete');
          updateGate();
        };
        img.onerror = function () { clear(); if (token === state.preview.token) failPreview(new Error('The preview image could not be loaded. Please try again.')); };
        img.src = data.previewUrl;
      } else { clear(); if (token === state.preview.token) failPreview(new Error('We could not generate your preview. Please try again.')); }
    }).catch(function (e) { clear(); if (token === state.preview.token) failPreview(e); });
  }
  function previewAlt() { return 'Preview of your Shared Miles print (' + (STYLE_NAME[state.design.styleId] || 'Linen') + ', ' + state.design.size + ').'; }
  function showPreviewWorking() { hidePreviewError(); hidePreviewNote(); show(el.posterLoading); }
  function hidePreviewNote() { if (el.posterLoadingNote) el.posterLoadingNote.hidden = true; }
  function failPreview(e) { state.preview.attempt++; if (state.preview.attempt < 2) { renderPreview(); return; } state.preview.attempt = 0; markPreviewError(e); }
  function markPreviewError(e) {
    hide(el.posterLoading); hidePreviewNote();
    state.preview.failed = true;
    if (el.previewErrorText) el.previewErrorText.textContent = (e && e.message) ? e.message : 'We could not update the preview.';
    show(el.previewError); updateGate();
  }
  function hidePreviewError() { hide(el.previewError); }
  // Resting poster state when no preview is due yet (fewer than two routes ready, or a
  // shared route was unshared): clear any working veil and restore the example. Without
  // this, the veil shown at import start spins forever after the FIRST route, because
  // renderPreview() bails until both routes are ready.
  function showExamplePoster() {
    hide(el.posterLoading); hidePreviewNote(); hidePreviewError();
    if (el.previewImg) { el.previewImg.hidden = true; el.previewImg.removeAttribute('src'); }
    show(el.posterEmpty); show(el.posterExample);
    state.preview.rendered = false; state.preview.failed = false;
    if (el.stage) el.stage.classList.remove('is-zoomable');
  }

  // ---- subtotal + gate + add to basket -------------------------------------
  function resolveVariant() {
    var variants = (product && product.variants) || [];
    if (!variants.length) return null;
    var size = state.design.size, pres = state.design.presentation, sizeRe = new RegExp('\\b' + size + '\\b', 'i');
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i], opts = [v.option1, v.option2, v.option3].filter(Boolean).map(String);
      if (opts.some(function (o) { return sizeRe.test(o); }) && opts.some(function (o) { return pres === 'framed' ? /fram/i.test(o) : /print/i.test(o); })) return v;
    }
    return variants.length === 1 ? variants[0] : null;
  }
  function digitalPrice() { return state.design.digitalCopy && typeof DIGITAL.price === 'number' ? DIGITAL.price : 0; }
  function renderTotals() {
    var v = resolveVariant(), html;
    if (!v || typeof v.price !== 'number') html = 'Confirmed at checkout';
    else {
      var dig = digitalPrice(), now = v.price + dig;
      var rrp = (typeof v.compare_at_price === 'number' && v.compare_at_price > v.price) ? v.compare_at_price : v.price;
      var was = rrp + dig;
      html = was > now ? ('<s class="sm__was">' + S.formatMoney(was) + '</s> <span class="sm__now">' + S.formatMoney(now) + '</span>') : S.formatMoney(now);
    }
    el.sumTotals.forEach(function (n) { if (n) n.innerHTML = html; });
  }
  function canAddToCart() { return bothReady() && state.preview.rendered && !state.preview.failed && !!resolveVariant(); }
  function updateGate() {
    renderTotals();
    if (state.addedToCart) { setPrimary('Add another for 50% off', false); return; }
    var enabled = canAddToCart();
    var label = enabled ? 'Add to basket' : 'Add to basket';
    setPrimary(label, !enabled);
    var hint = '';
    if (!bothReady()) hint = 'Add a route for both people to continue.';
    else if (state.preview.failed) hint = 'We could not generate your preview. Use Try again.';
    else if (!state.preview.rendered) hint = 'Generating your preview…';
    else if (!resolveVariant()) hint = 'This size and finish are not available. Please choose another.';
    setBarInfo(hint);
  }
  function setPrimary(label, disabled) {
    el.primaries.forEach(function (b) { b.disabled = disabled || b.classList.contains('is-busy'); });
    el.primaryLabels.forEach(function (n) { if (n) n.textContent = label; });
  }
  function setBarInfo(msg) { if (!el.barError) return; if (!msg) { el.barError.hidden = true; return; } el.barError.classList.add('is-info'); el.barError.setAttribute('aria-live', 'polite'); el.barError.textContent = msg; el.barError.hidden = false; }
  function setBarError(msg) { if (!el.barError) return; el.barError.classList.remove('is-info'); el.barError.setAttribute('aria-live', 'assertive'); el.barError.textContent = msg; el.barError.hidden = false; }

  function addToCart() {
    if (!canAddToCart()) return;
    var variant = resolveVariant();
    setBusy(true);
    track('add_to_cart_attempt');
    var lockData = null, lineCount = 1;
    flushDesign()
      .then(function () { var c = resolveVariant(); if (c) variant = c; if (!variant) throw new Error('That size and finish are not available.'); return client.request('/jobs/' + state.jobId + '/lock-for-cart', { method: 'POST', timeoutMs: 35000 }); })
      .then(function (data) {
        lockData = data;
        var printProps = {}; for (var k in data.properties) { if (Object.prototype.hasOwnProperty.call(data.properties, k)) printProps[k] = data.properties[k]; }
        if (el.giftToggle && el.giftToggle.checked) { printProps['Gift'] = 'Yes'; var gm = el.giftMessage && el.giftMessage.value ? el.giftMessage.value.trim() : ''; if (gm) printProps['Gift message'] = gm.slice(0, 200); }
        var items = [{ id: variant.id, quantity: 1, properties: printProps }];
        if (state.design.digitalCopy && DIGITAL.variantId) {
          var digProps = { 'Digital copy for': (state.design.text.title || 'your Shared Miles print') };
          digProps['_rps_digital_for'] = state.jobId;
          if (data.properties && data.properties['_rps_preview']) digProps['_rps_preview'] = data.properties['_rps_preview'];
          items.push({ id: DIGITAL.variantId, quantity: 1, properties: digProps });
        }
        lineCount = items.length;
        var sig = printProps['_route_print_job_signature'];
        return S.cart.hasSignature(sig).then(function (already) { return already ? null : S.cart.add(items); });
      })
      .then(function (cartData) {
        if (lockData && lockData.continuation && lockData.continuation.publicId) { state.jobId = lockData.continuation.publicId; SESSION.set({ jobId: state.jobId }); }
        state.addedToCart = true; state.addingToCart = false; SESSION.clear();
        S.cart.notifyUpdated(cartData || {}, lineCount, 'shared-miles');
        track('add_to_cart_success');
        el.primaries.forEach(function (b) { b.classList.remove('is-busy'); });
        setBarInfo('Added to your basket. Add another and the second is 50% off at checkout.');
        setPrimary('Add another for 50% off', false);
      })
      .catch(function (e) { setBusy(false); var m = (e && e.message) || 'Could not add to basket. Please try again.'; setBarError(m); track('add_to_cart_error', { code: (e && e.code) || 'error' }); });
  }
  function setBusy(busy) {
    el.primaries.forEach(function (b) { b.classList.toggle('is-busy', busy); b.disabled = busy; });
    state.addingToCart = busy;
    if (busy) { el.primaryLabels.forEach(function (n) { if (n) n.textContent = 'Adding…'; }); if (el.barError) el.barError.hidden = true; }
    else updateGate();
  }
  function addAnother() { window.location.href = window.location.origin + window.location.pathname; }

  // ---- Strava OAuth return -------------------------------------------------
  function handleStravaReturn() {
    var params; try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var status = params.get('rps_strava'); if (!status) return;
    var jobId = params.get('jobId');
    var stash = SESSION.get();
    params.delete('rps_strava'); params.delete('jobId'); params.delete('reason');
    var rest = params.toString();
    try { window.history.replaceState({}, document.title, window.location.pathname + (rest ? '?' + rest : '') + window.location.hash); } catch (e) {}
    if (status === 'connected' && jobId) {
      state.jobId = jobId;
      // Re-adopt the full job (participants + design) so the slot that triggered the
      // connect can import into the right participant.
      client.request('/jobs/' + jobId).then(function (st) {
        adoptServerJob(st, stash);
        var slot = (stash && typeof stash.activeSlot === 'number') ? stash.activeSlot : 0;
        state.activeSlot = slot;
        openSource(slot, 'strava');
      }).catch(function () { /* stale job: start fresh */ });
    }
  }
  // Re-hydrate slots + design from a server job state (used by Strava return + resume).
  function adoptServerJob(st, stash) {
    if (!st) return;
    state.ready = Promise.resolve(state.jobId);
    if (Array.isArray(st.participants)) {
      st.participants.forEach(function (sp, i) {
        if (!state.participants[i]) state.participants[i] = newParticipant(i);
        var p = state.participants[i];
        p.id = sp.id; p.hasRoute = sp.hasRoute; p.sameAs = !!sp.sameAs; p.activityCount = sp.activityCount || 0;
        var act = sp.activities && sp.activities[0]; p.source = act ? act.sourceType : null; p.routeName = act ? act.routeName : null; p.distanceMetres = act && typeof act.distanceMetres === 'number' ? act.distanceMetres : null;
      });
    }
    var d = st.design || (stash && stash.design && { }) || {};
    if (stash && stash.design) { state.design = Object.assign(state.design, { styleId: stash.design.styleId || state.design.styleId }); }
    if (d.styleId) selectStyle(d.styleId);
    renderSlots(); updateGate();
    if (bothReady()) { setStep && setStep(); renderPreview(); }
  }
  function tryRestore() {
    var s = SESSION.get(); if (!s || !s.jobId || state.jobId) return;
    try { if (new URLSearchParams(window.location.search).get('rps_strava')) return; } catch (e) {}
    client.request('/jobs/' + s.jobId).then(function (st) {
      var editable = st && ['draft', 'route_imported', 'preview_ready'].indexOf(st.status) !== -1;
      if (!editable || st.kind !== 'shared-miles') { SESSION.clear(); return; }
      state.jobId = s.jobId; adoptServerJob(st, s);
    }).catch(function () { /* stale: fresh start */ });
  }

  // ---- zoom lightbox -------------------------------------------------------
  function openZoom() { if (!el.zoom || !el.zoomImg || !state.preview.rendered) return; el.zoomImg.src = el.previewImg.currentSrc || el.previewImg.src; el.zoom.hidden = false; document.body.classList.add('sm-zoom-open'); }
  function closeZoom() { if (!el.zoom) return; el.zoom.hidden = true; document.body.classList.remove('sm-zoom-open'); if (el.zoomImg) el.zoomImg.removeAttribute('src'); }

  // ---- init ----------------------------------------------------------------
  function init() {
    // Story modes
    if (el.modeRow) { qsa('[data-sm-mode]', el.modeRow).forEach(function (b) { b.addEventListener('click', function () { selectMode(b.dataset.smMode); }); }); S.wireRadioGroup(el.modeRow, '[data-sm-mode]', function (t) { selectMode(t.dataset.smMode); }); }
    selectMode(state.story.mode);
    renderSlots();
    // Picker
    if (el.pickerClose) el.pickerClose.addEventListener('click', closePicker);
    if (el.stravaReconnect) el.stravaReconnect.addEventListener('click', function () { connectStrava(state.activeSlot); });
    if (el.stravaSearch) el.stravaSearch.addEventListener('input', S.debounce(function () { state.strava.search = el.stravaSearch.value; renderActivities(); }, 150));
    if (el.popularSearch) el.popularSearch.addEventListener('input', S.debounce(function () { state.popular.search = el.popularSearch.value; renderPopular(); }, 150));
    if (el.drawAdd) el.drawAdd.addEventListener('click', function () { if (state.draw.map) addPin(state.draw.map.getCenter()); });
    if (el.drawUndo) el.drawUndo.addEventListener('click', function () { if (state.draw.pins.length) { state.draw.pins.pop(); rebuildPins(); scheduleSnap(); } });
    if (el.drawClear) el.drawClear.addEventListener('click', function () { state.draw.pins = []; if (state.draw.snapLine) state.draw.layer.removeLayer(state.draw.snapLine); rebuildPins(); scheduleSnap(); });
    if (el.drawUse) el.drawUse.addEventListener('click', useDrawn);
    // Styles + pairs
    if (el.styleRow) { qsa('[data-sm-style]', el.styleRow).forEach(function (b) { b.addEventListener('click', function () { selectStyle(b.dataset.smStyle); }); }); S.wireRadioGroup(el.styleRow, '[data-sm-style]', function (t) { selectStyle(t.dataset.smStyle); }); }
    if (el.pairRow) { qsa('[data-sm-pair]', el.pairRow).forEach(function (b) { b.addEventListener('click', function () { selectPair(b.dataset.smPair); }); }); S.wireRadioGroup(el.pairRow, '[data-sm-pair]', function (t) { selectPair(t.dataset.smPair); }); }
    selectStyle(state.design.styleId); selectPair(state.story.colourPreset);
    // Text fields
    qsa('[data-sm-text]').forEach(function (input) { input.addEventListener('input', function () { var key = input.dataset.smText; state.design.text[key] = input.value; state.edited[key] = true; scheduleSave(); }); });
    // Markers
    if (el.markersRow) qsa('[data-sm-marker]', el.markersRow).forEach(function (c) { c.addEventListener('change', function () { state.story.markers[c.dataset.smMarker] = c.checked; scheduleSave(); }); });
    // Units
    if (el.unitRow) { qsa('[data-sm-unit]', el.unitRow).forEach(function (b) { b.addEventListener('click', function () { selectUnit(b.dataset.smUnit); }); }); S.wireRadioGroup(el.unitRow, '[data-sm-unit]', function (t) { selectUnit(t.dataset.smUnit); }); }
    syncUnitUI();
    // Size / presentation / digital
    if (el.sizeRow) { qsa('[data-sm-size]', el.sizeRow).forEach(function (b) { b.addEventListener('click', function () { selectSize(b.dataset.smSize); }); }); S.wireRadioGroup(el.sizeRow, '[data-sm-size]', function (t) { selectSize(t.dataset.smSize); }); }
    if (el.presentationRow) { qsa('[data-sm-presentation]', el.presentationRow).forEach(function (b) { b.addEventListener('click', function () { selectPresentation(b.dataset.smPresentation); }); }); S.wireRadioGroup(el.presentationRow, '[data-sm-presentation]', function (t) { selectPresentation(t.dataset.smPresentation); }); }
    selectSize(state.design.size); selectPresentation(state.design.presentation);
    var digitalAvailable = !!(DIGITAL.variantId && DIGITAL.price);
    if (el.digital && digitalAvailable) { el.digital.addEventListener('change', function () { state.design.digitalCopy = el.digital.checked; renderTotals(); scheduleSave(false, false); }); if (el.addonPrice && DIGITAL.price) { el.addonPrice.textContent = ' +' + S.formatMoney(DIGITAL.price); el.addonPrice.hidden = false; } }
    else { state.design.digitalCopy = false; if (el.digital) { el.digital.checked = false; el.digital.disabled = true; } if (el.digitalRow) el.digitalRow.hidden = true; }
    // Gift
    if (el.giftToggle && el.giftFields) el.giftToggle.addEventListener('change', function () { el.giftFields.hidden = !el.giftToggle.checked; });
    // Preview views + zoom + retry
    qsa('[data-sm-view]').forEach(function (b) { b.addEventListener('click', function () { qsa('[data-sm-view]').forEach(function (x) { x.classList.toggle('is-active', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); }); if (el.stage) el.stage.classList.toggle('is-wall', b.dataset.smView === 'wall'); }); });
    if (el.poster) el.poster.addEventListener('click', function () { if (state.preview.rendered) openZoom(); });
    if (el.zoom) el.zoom.addEventListener('click', function (e) { if (e.target !== el.zoomImg) closeZoom(); });
    document.addEventListener('keydown', function (e) { if ((e.key === 'Escape' || e.key === 'Esc') && el.zoom && !el.zoom.hidden) closeZoom(); });
    if (el.previewRetry) el.previewRetry.addEventListener('click', function () { hidePreviewError(); if (!bothReady()) { show(el.posterExample); show(el.posterEmpty); hide(el.posterLoading); return; } state.preview.attempt = 0; scheduleSave(true); });
    if (el.previewImg) { el.previewImg.setAttribute('draggable', 'false'); el.previewImg.addEventListener('contextmenu', function (e) { e.preventDefault(); }); el.previewImg.addEventListener('dragstart', function (e) { e.preventDefault(); }); }
    // Primary action
    el.primaries.forEach(function (b) { b.addEventListener('click', function () { if (b.disabled) return; if (state.addedToCart) { addAnother(); return; } addToCart(); }); });
    // bar height clearance
    if (window.ResizeObserver && el.bar) { try { var ro = new ResizeObserver(function () { var h = el.bar.offsetHeight; if (h) root.style.setProperty('--sm-bar-h', Math.ceil(h) + 'px'); }); ro.observe(el.bar); } catch (e) {} }
    updateGate();
    track('editor_open');
    handleStravaReturn();
    tryRestore();
  }
  // `setStep` referenced defensively in adoptServerJob (single-scroll layout has none).
  var setStep = null;
  init();
})();
